import type { ClientViewFilter } from "@repo/shared/types";
import { ListFilter, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  type DraftFilterValue,
  type FilterField,
  FilterValueEditor,
  pickerOptions,
  resolveFilterField,
  valuePickerLabels,
} from "@/components/clients/filters";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ValuePickerDialog } from "@/components/ui/value-picker-dialog";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./filter-bar.i18n";

type FieldRow = { kind: "field"; key: string; draft: DraftFilterValue };
/** A URL filter the registry cannot resolve (unknown or deactivated field) — shown, removable, and otherwise kept as is. */
type OpaqueRow = { kind: "opaque"; filter: ClientViewFilter };
type Row = FieldRow | OpaqueRow;

/** Build this bar's initial rows from the URL's saved filters — the mirror of `field.toFilter`. */
function rowsFromFilters(filters: ClientViewFilter[], registry: FilterField[]): Row[] {
  return filters.map((f): Row => {
    const resolved = resolveFilterField(f, registry);
    return resolved
      ? { kind: "field", key: resolved.field.key, draft: resolved.draft }
      : { kind: "opaque", filter: f };
  });
}

/**
 * QL-349 — the ad-hoc filter bar on `/clients`. Available to every user (no
 * designer permission gate) and cannot be saved — only Settings → Client
 * Views persists a filter. Combines (ANDs) with the selected view; changing
 * anything here is the caller's job to push into the URL and reset the page
 * (see `useClientListUrlState`), this component only reports the resulting
 * `ClientViewFilter[]`.
 *
 * Rows are local UI state seeded from `filters` once on mount — not
 * re-derived from it on every render — so a filter mid-edit (a field picked,
 * no value yet) can exist without being a complete, URL-worthy
 * `ClientViewFilter`. Every change recomputes the complete filters and
 * reports them via `onFiltersChange`.
 *
 * Renders as `display: contents` (QL-512): its filter rows and the "Add
 * filter" button join the caller's toolbar row, after the search box, rather
 * than taking a row of their own — so with no filters there is no extra line.
 */
export function FilterBar({
  filters,
  onFiltersChange,
  registry,
  userLanguage,
}: {
  filters: ClientViewFilter[];
  onFiltersChange: (filters: ClientViewFilter[]) => void;
  registry: FilterField[];
  userLanguage: string;
}) {
  const t = useT(T);
  const [rows, setRows] = useState<Row[]>(() => rowsFromFilters(filters, registry));
  // The filters this bar last reported. A `filters` prop that differs from it
  // changed from outside (nav link, back/forward) — the rows are re-seeded so
  // the chips never show a filter the list is no longer using.
  const lastCommitted = useRef(JSON.stringify(filters));
  useEffect(() => {
    const incoming = JSON.stringify(filters);
    if (incoming === lastCommitted.current) return;
    lastCommitted.current = incoming;
    setRows(rowsFromFilters(filters, registry));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed only when the URL's filters change, not whenever the registry is rebuilt
  }, [filters]);
  const [valuesDialogOpen, setValuesDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const fieldByKey = new Map(registry.map((f) => [f.key, f]));
  const shownKeys = new Set(rows.flatMap((r) => (r.kind === "field" ? [r.key] : [])));
  const standardFields = registry.filter((f) => f.group === "standard" && !shownKeys.has(f.key));
  const customFields = registry.filter((f) => f.group === "custom" && !shownKeys.has(f.key));

  const commit = (nextRows: Row[]) => {
    setRows(nextRows);
    const nextFilters = nextRows
      .map((row) =>
        row.kind === "opaque" ? row.filter : (fieldByKey.get(row.key)?.toFilter(row.draft) ?? null),
      )
      .filter((f): f is ClientViewFilter => f !== null);
    lastCommitted.current = JSON.stringify(nextFilters);
    onFiltersChange(nextFilters);
  };

  const addFilter = (key: string) => commit([...rows, { kind: "field", key, draft: {} }]);
  const removeFilter = (index: number) => commit(rows.filter((_, i) => i !== index));
  const changeDraft = (index: number, patch: DraftFilterValue) => {
    const row = rows[index];
    if (row.kind !== "field") return;
    const next = [...rows];
    next[index] = { ...row, draft: { ...row.draft, ...patch } };
    commit(next);
  };

  const editingRow = editingIndex !== null ? rows[editingIndex] : undefined;
  const editingFieldRow = editingRow?.kind === "field" ? editingRow : undefined;
  const editingField = editingFieldRow ? fieldByKey.get(editingFieldRow.key) : undefined;

  return (
    <div className="contents">
      {rows.map((row, index) => {
        if (row.kind === "opaque") {
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: opaque rows have no stable key
            <div key={`opaque-${index}`} className="flex items-center gap-1">
              <span className="text-sm text-muted-foreground">{t.unavailableFilter}</span>
              <button
                type="button"
                onClick={() => removeFilter(index)}
                aria-label={t.remove}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          );
        }
        const field = fieldByKey.get(row.key);
        if (!field) return null;
        return (
          <div key={row.key} className="space-y-1">
            <div className="flex items-center gap-1">
              <span className="text-sm text-muted-foreground">{field.label}</span>
              <button
                type="button"
                onClick={() => removeFilter(index)}
                aria-label={t.remove}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
            <FilterValueEditor
              field={field}
              draft={row.draft}
              onChange={(patch) => changeDraft(index, patch)}
              onOpenValuesDialog={() => {
                setEditingIndex(index);
                setValuesDialogOpen(true);
              }}
              userLanguage={userLanguage}
            />
          </div>
        );
      })}

      {(standardFields.length > 0 || customFields.length > 0) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" className="gap-2">
              <ListFilter className="h-4 w-4" />
              {t.addFilter}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {standardFields.length > 0 && (
              <>
                <DropdownMenuLabel>{t.standardFields}</DropdownMenuLabel>
                {standardFields.map((f) => (
                  <DropdownMenuItem key={f.key} onSelect={() => addFilter(f.key)}>
                    {f.label}
                  </DropdownMenuItem>
                ))}
              </>
            )}
            {standardFields.length > 0 && customFields.length > 0 && <DropdownMenuSeparator />}
            {customFields.length > 0 && (
              <>
                <DropdownMenuLabel>{t.clientFields}</DropdownMenuLabel>
                {customFields.map((f) => (
                  <DropdownMenuItem key={f.key} onSelect={() => addFilter(f.key)}>
                    {f.label}
                  </DropdownMenuItem>
                ))}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {editingField && editingFieldRow && (
        <ValuePickerDialog
          open={valuesDialogOpen}
          options={pickerOptions(
            editingField.options,
            editingFieldRow.draft.values ?? [],
            editingField.unresolvedLabel,
          )}
          selectedValues={editingFieldRow.draft.values ?? []}
          labels={valuePickerLabels(userLanguage, editingField.label)}
          onClose={() => {
            setValuesDialogOpen(false);
            setEditingIndex(null);
          }}
          onSave={(values) => {
            changeDraft(editingIndex as number, { values });
            setValuesDialogOpen(false);
            setEditingIndex(null);
          }}
        />
      )}
    </div>
  );
}
